import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import './Walkthrough.css'

/**
 * Walkthrough — reusable guided-tour overlay.
 *
 * Renders a semi-transparent backdrop that "spotlights" the current
 * step's target element with a tooltip card next to it. The user
 * clicks Next / Back / Skip to advance. Esc dismisses.
 *
 * Each step is:
 *   {
 *     target: string,        // CSS selector for the DOM node to spotlight
 *     title: string,
 *     body: string,
 *     placement?: 'top' | 'bottom' | 'left' | 'right',
 *     waitForNav?: string,   // route to navigate to before entering this step
 *   }
 *
 * If a target can't be found within ~2s of polling, the step falls
 * back to a centered tooltip explaining it so the tour doesn't lock up.
 */
function Walkthrough({ steps, onClose }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [stepIndex, setStepIndex] = useState(0)
  const [rect, setRect] = useState(null) // bounding rect of current target
  const [missing, setMissing] = useState(false) // true when target was not found in time
  const pollRafRef = useRef(null)
  const scrollTimerRef = useRef(null)

  const step = steps[stepIndex]
  const isLast = stepIndex === steps.length - 1
  const isFirst = stepIndex === 0

  const close = useCallback(() => {
    onClose?.()
  }, [onClose])

  // Esc dismisses
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [close])

  // If this step requests a navigation first, do it before polling.
  useEffect(() => {
    if (step?.waitForNav && location.pathname !== step.waitForNav) {
      navigate(step.waitForNav)
    }
    // We intentionally only run this on step changes, not location changes
    // (the subsequent effect handles waiting for the DOM to catch up).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex])

  // Poll for the target element. Compute its bounding rect, scroll into
  // view, and record. If not found within ~2s, mark the step missing and
  // show a centered fallback tooltip.
  useEffect(() => {
    if (!step) return
    setRect(null)
    setMissing(false)

    const start = performance.now()
    const TIMEOUT_MS = 2000
    let scrolled = false

    const tick = () => {
      const el = document.querySelector(step.target)
      if (el) {
        if (!scrolled) {
          try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }) } catch { /* noop */ }
          scrolled = true
        }
        const r = el.getBoundingClientRect()
        // After scrollIntoView fires, the first rect may still be
        // off-screen; keep polling for a few frames until it stabilizes.
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
      }

      if (performance.now() - start >= TIMEOUT_MS) {
        if (!el) setMissing(true)
        return
      }
      pollRafRef.current = requestAnimationFrame(tick)
    }

    pollRafRef.current = requestAnimationFrame(tick)
    return () => {
      if (pollRafRef.current) cancelAnimationFrame(pollRafRef.current)
    }
  }, [stepIndex, step, location.pathname])

  // Reposition on window resize + scroll (debounced via rAF).
  useEffect(() => {
    if (!step) return
    const reposition = () => {
      const el = document.querySelector(step.target)
      if (!el) return
      const r = el.getBoundingClientRect()
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
    }
    const onEvent = () => {
      if (scrollTimerRef.current) cancelAnimationFrame(scrollTimerRef.current)
      scrollTimerRef.current = requestAnimationFrame(reposition)
    }
    window.addEventListener('resize', onEvent)
    window.addEventListener('scroll', onEvent, true)
    return () => {
      window.removeEventListener('resize', onEvent)
      window.removeEventListener('scroll', onEvent, true)
      if (scrollTimerRef.current) cancelAnimationFrame(scrollTimerRef.current)
    }
  }, [step])

  if (!step) return null

  const next = () => {
    // Let the step hook into Next — useful when advancing requires a
    // DOM action in the app (e.g., clicking a + button to open a
    // modal whose fields the NEXT step wants to spotlight).
    try { step.onNext?.() } catch (e) { console.warn('walkthrough onNext failed', e) }
    if (isLast) {
      close()
    } else {
      setStepIndex(i => i + 1)
    }
  }
  const back = () => {
    if (!isFirst) setStepIndex(i => i - 1)
  }

  // Figure out tooltip position. Simple: pick one of top/bottom/left/right
  // and offset ~12px. If no rect, center on screen.
  const TOOLTIP_WIDTH = 320
  const TOOLTIP_OFFSET = 12
  let tooltipStyle = {}
  if (rect) {
    const placement = step.placement || 'bottom'
    const vw = window.innerWidth
    const vh = window.innerHeight
    if (placement === 'bottom') {
      tooltipStyle = {
        top: rect.top + rect.height + TOOLTIP_OFFSET,
        left: Math.max(12, Math.min(vw - TOOLTIP_WIDTH - 12, rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2)),
      }
    } else if (placement === 'top') {
      tooltipStyle = {
        top: rect.top - TOOLTIP_OFFSET,
        left: Math.max(12, Math.min(vw - TOOLTIP_WIDTH - 12, rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2)),
        transform: 'translateY(-100%)',
      }
    } else if (placement === 'left') {
      tooltipStyle = {
        top: Math.max(12, Math.min(vh - 120, rect.top + rect.height / 2 - 60)),
        left: rect.left - TOOLTIP_OFFSET,
        transform: 'translateX(-100%)',
      }
    } else {
      // right
      tooltipStyle = {
        top: Math.max(12, Math.min(vh - 120, rect.top + rect.height / 2 - 60)),
        left: rect.left + rect.width + TOOLTIP_OFFSET,
      }
    }
  } else {
    tooltipStyle = {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    }
  }

  const showSpotlight = rect && !missing
  const stepCounter = `Step ${stepIndex + 1} of ${steps.length}`

  return (
    <div className="walkthrough-root" role="dialog" aria-modal="true" aria-label="Walkthrough">
      {/* Fallback full backdrop when we don't have a spotlight (missing target). */}
      {!showSpotlight && <div className="walkthrough-backdrop" />}

      {/* Spotlight: a transparent div sized to the target with a huge
          box-shadow that darkens the rest of the screen. */}
      {showSpotlight && (
        <div
          className="walkthrough-spotlight"
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
          }}
        />
      )}

      <div className="walkthrough-tooltip" style={tooltipStyle}>
        <div className="walkthrough-tooltip-counter">{stepCounter}</div>
        <h3 className="walkthrough-tooltip-title">{step.title}</h3>
        <p className="walkthrough-tooltip-body">
          {missing
            ? "We couldn't find that spot right now — click Next to continue the tour, or Skip to exit."
            : step.body}
        </p>
        <div className="walkthrough-tooltip-actions">
          <button
            type="button"
            className="walkthrough-btn walkthrough-btn-ghost"
            onClick={close}
          >
            Skip tour
          </button>
          <div className="walkthrough-tooltip-actions-right">
            <button
              type="button"
              className="walkthrough-btn walkthrough-btn-outline"
              onClick={back}
              disabled={isFirst}
            >
              Back
            </button>
            <button
              type="button"
              className="walkthrough-btn walkthrough-btn-primary"
              onClick={next}
            >
              {isLast ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Walkthrough
